import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Database,
  Plus,
  Trash2,
  Save,
  ChevronDown,
  X,
  Check,
  ToggleLeft,
  ToggleRight,
  Edit3,
  Tag,
  AlertTriangle,
  RefreshCw,
  Download,
  Upload,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const AUTH_URL = process.env.REACT_APP_AUTH_URL || "/auth";

const FIELD_TYPES = ["string", "integer", "boolean"];

const TYPE_BADGE = {
  string: "bg-blue-100 text-blue-700",
  integer: "bg-amber-100 text-amber-700",
  boolean: "bg-violet-100 text-violet-700",
};

const defaultForm = {
  field_name: "",
  field_type: "string",
  required: false,
  description: "",
  display_order: 0,
  allowed_values_raw: "",
};

const MetadataSchemaManager = () => {
  const { token } = useAuth();
  const [verticals, setVerticals] = useState([]);
  const [selectedVertical, setSelectedVertical] = useState(null);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [verticalsLoading, setVerticalsLoading] = useState(true);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [submitting, setSubmitting] = useState(false);

  // Toast state
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  // Inline editing state
  const [editingDescription, setEditingDescription] = useState(null); // field id
  const [descriptionDraft, setDescriptionDraft] = useState("");
  const [editingOrder, setEditingOrder] = useState(null);
  const [orderDraft, setOrderDraft] = useState("");
  const [savingField, setSavingField] = useState(null);

  // Allowed values inline add
  const [addingValueFor, setAddingValueFor] = useState(null);
  const [newValueDraft, setNewValueDraft] = useState("");

  // Delete confirmation
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // CSV import
  const importFileRef = useRef(null);
  const [importRows, setImportRows] = useState(null); // null = closed, [] = parsed rows
  const [importLoading, setImportLoading] = useState(false);
  const [importProgress, setImportProgress] = useState(0);

  const headers = useCallback(
    () => ({
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    }),
    [token],
  );

  const showToast = useCallback((message, type = "success") => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ message, type });
    toastTimer.current = setTimeout(() => setToast(null), 3500);
  }, []);

  // Load verticals on mount
  useEffect(() => {
    const loadVerticals = async () => {
      setVerticalsLoading(true);
      try {
        const res = await fetch(`${AUTH_URL}/rag/metadata-schema/verticals`, {
          headers: headers(),
        });
        if (!res.ok) throw new Error("Failed to load verticals");
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        setVerticals(list);
        if (list.length > 0) setSelectedVertical(list[0].vertical_id);
      } catch (e) {
        showToast("Failed to load verticals", "error");
      } finally {
        setVerticalsLoading(false);
      }
    };
    loadVerticals();
  }, [headers, showToast]);

  // Load fields when vertical changes
  const loadFields = useCallback(async () => {
    if (!selectedVertical) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${AUTH_URL}/rag/metadata-schema?vertical_id=${selectedVertical}`,
        { headers: headers() },
      );
      if (!res.ok) throw new Error("Failed to load fields");
      const data = await res.json();
      setFields(Array.isArray(data) ? data : []);
    } catch (e) {
      showToast("Failed to load schema fields", "error");
    } finally {
      setLoading(false);
    }
  }, [selectedVertical, headers, showToast]);

  useEffect(() => {
    loadFields();
  }, [loadFields]);

  // PUT helper
  const putField = useCallback(
    async (id, body) => {
      setSavingField(id);
      try {
        const res = await fetch(`${AUTH_URL}/rag/metadata-schema/field/${id}`, {
          method: "PUT",
          headers: headers(),
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error("Save failed");
        showToast("Field updated");
        await loadFields();
      } catch (e) {
        showToast("Failed to save field", "error");
      } finally {
        setSavingField(null);
      }
    },
    [headers, showToast, loadFields],
  );

  // Toggle is_active
  const toggleActive = useCallback(
    (field) => {
      putField(field.id, { is_active: !field.is_active });
    },
    [putField],
  );

  // Commit description edit
  const commitDescription = useCallback(
    (field) => {
      setEditingDescription(null);
      if (descriptionDraft !== field.description) {
        putField(field.id, { description: descriptionDraft });
      }
    },
    [descriptionDraft, putField],
  );

  // Commit display_order edit
  const commitOrder = useCallback(
    (field) => {
      setEditingOrder(null);
      const parsed = parseInt(orderDraft, 10);
      if (!isNaN(parsed) && parsed !== field.display_order) {
        putField(field.id, { display_order: parsed });
      }
    },
    [orderDraft, putField],
  );

  // Remove an allowed value
  const removeAllowedValue = useCallback(
    (field, valueToRemove) => {
      const updated = (field.allowed_values || []).filter(
        (v) => v !== valueToRemove,
      );
      putField(field.id, { allowed_values: updated.length ? updated : null });
    },
    [putField],
  );

  // Add an allowed value
  const addAllowedValue = useCallback(
    (field) => {
      const trimmed = newValueDraft.trim();
      if (!trimmed) return;
      const existing = field.allowed_values || [];
      if (existing.includes(trimmed)) {
        showToast("Value already exists", "error");
        return;
      }
      const updated = [...existing, trimmed];
      setNewValueDraft("");
      setAddingValueFor(null);
      putField(field.id, { allowed_values: updated });
    },
    [newValueDraft, putField, showToast],
  );

  // Delete field
  const deleteField = useCallback(
    async (id) => {
      setConfirmDeleteId(null);
      setSavingField(id);
      try {
        const res = await fetch(`${AUTH_URL}/rag/metadata-schema/field/${id}`, {
          method: "DELETE",
          headers: headers(),
        });
        if (!res.ok) throw new Error("Delete failed");
        showToast("Field deleted");
        await loadFields();
      } catch (e) {
        showToast("Failed to delete field", "error");
      } finally {
        setSavingField(null);
      }
    },
    [headers, showToast, loadFields],
  );

  // Add field POST
  const submitAddField = useCallback(
    async (e) => {
      e.preventDefault();
      if (!form.field_name.trim()) {
        showToast("Field name is required", "error");
        return;
      }
      setSubmitting(true);
      try {
        const allowedValues = form.allowed_values_raw
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean);
        const body = {
          vertical_id: selectedVertical,
          field_name: form.field_name.trim(),
          field_type: form.field_type,
          required: form.required,
          description: form.description.trim(),
          display_order: parseInt(form.display_order, 10) || 0,
          allowed_values: allowedValues.length ? allowedValues : null,
        };
        const res = await fetch(`${AUTH_URL}/rag/metadata-schema/field`, {
          method: "POST",
          headers: headers(),
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || "Failed to add field");
        }
        showToast("Field added successfully");
        setShowModal(false);
        setForm(defaultForm);
        await loadFields();
      } catch (e) {
        showToast(e.message || "Failed to add field", "error");
      } finally {
        setSubmitting(false);
      }
    },
    [form, selectedVertical, headers, showToast, loadFields],
  );

  // ── CSV helpers ──────────────────────────────────────────────────────────

  const escapeCSV = (val) => {
    const s = val == null ? "" : String(val);
    if (s.includes(",") || s.includes('"') || s.includes("\n"))
      return '"' + s.replace(/"/g, '""') + '"';
    return s;
  };

  const parseCSVLine = (line) => {
    const fields = [];
    let i = 0;
    while (i <= line.length) {
      if (line[i] === '"') {
        let j = i + 1;
        let field = "";
        while (j < line.length) {
          if (line[j] === '"' && line[j + 1] === '"') {
            field += '"';
            j += 2;
          } else if (line[j] === '"') {
            j++;
            break;
          } else field += line[j++];
        }
        fields.push(field);
        if (line[j] === ",") j++;
        i = j;
      } else {
        let j = i;
        while (j < line.length && line[j] !== ",") j++;
        fields.push(line.slice(i, j));
        i = j + 1;
      }
    }
    return fields;
  };

  const handleExport = useCallback(() => {
    if (!fields.length) return;
    const cols = [
      "field_name",
      "field_type",
      "required",
      "description",
      "display_order",
      "is_active",
      "allowed_values",
    ];
    const rows = [cols.join(",")];
    fields.forEach((f) => {
      rows.push(
        [
          escapeCSV(f.field_name),
          escapeCSV(f.field_type),
          escapeCSV(f.required ? "true" : "false"),
          escapeCSV(f.description || ""),
          escapeCSV(f.display_order ?? 0),
          escapeCSV(f.is_active ? "true" : "false"),
          escapeCSV((f.allowed_values || []).join("|")),
        ].join(","),
      );
    });
    const blob = new Blob([rows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${selectedVertical}_schema.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [fields, selectedVertical]);

  const handleImportFile = useCallback(
    (e) => {
      const file = e.target.files[0];
      e.target.value = "";
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const lines = evt.target.result.trim().split(/\r?\n/);
          if (lines.length < 2) {
            showToast("CSV has no data rows", "error");
            return;
          }
          const headers = parseCSVLine(lines[0]).map((h) =>
            h.trim().toLowerCase(),
          );
          const nameIdx = headers.indexOf("field_name");
          if (nameIdx === -1) {
            showToast("CSV must have a field_name column", "error");
            return;
          }
          const parsed = lines
            .slice(1)
            .filter((l) => l.trim())
            .map((line) => {
              const vals = parseCSVLine(line);
              const get = (col) => (vals[headers.indexOf(col)] || "").trim();
              const parseBool = (s, def) =>
                s === "" ? def : s === "true" || s === "1";
              return {
                field_name: get("field_name"),
                field_type: get("field_type") || "string",
                required: parseBool(get("required"), false),
                description: get("description"),
                display_order: parseInt(get("display_order"), 10) || 0,
                is_active: parseBool(get("is_active"), true),
                allowed_values: get("allowed_values")
                  ? get("allowed_values")
                      .split("|")
                      .map((v) => v.trim())
                      .filter(Boolean)
                  : null,
              };
            })
            .filter((r) => r.field_name);
          if (!parsed.length) {
            showToast("No valid rows found in CSV", "error");
            return;
          }
          setImportRows(parsed);
        } catch {
          showToast("Failed to parse CSV", "error");
        }
      };
      reader.readAsText(file);
    },
    [showToast],
  );

  const submitImport = useCallback(async () => {
    if (!importRows?.length) return;
    setImportLoading(true);
    setImportProgress(0);
    let succeeded = 0;
    let failed = 0;
    for (let i = 0; i < importRows.length; i++) {
      const row = importRows[i];
      try {
        const res = await fetch(`${AUTH_URL}/rag/metadata-schema/field`, {
          method: "POST",
          headers: headers(),
          body: JSON.stringify({ ...row, vertical_id: selectedVertical }),
        });
        if (res.ok) succeeded++;
        else failed++;
      } catch {
        failed++;
      }
      setImportProgress(i + 1);
    }
    setImportLoading(false);
    setImportRows(null);
    showToast(
      failed === 0
        ? `Imported ${succeeded} field${succeeded !== 1 ? "s" : ""}`
        : `Imported ${succeeded}, failed ${failed}`,
      failed === 0 ? "success" : "error",
    );
    await loadFields();
  }, [importRows, selectedVertical, headers, showToast, loadFields]);

  // ─────────────────────────────────────────────────────────────────────────

  const selectedVerticalName =
    verticals.find((v) => v.vertical_id === selectedVertical)?.vertical_name ||
    "";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 rounded-lg">
            <Database className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-800">
              Metadata Schema Manager
            </h2>
            <p className="text-sm text-slate-500">
              Define per-vertical field schemas for RAG document metadata
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadFields}
            disabled={loading}
            className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={handleExport}
            disabled={!fields.length}
            className="flex items-center gap-2 px-3 py-2 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 transition-colors text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            title="Export schema to CSV"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
          <button
            onClick={() => importFileRef.current?.click()}
            disabled={!selectedVertical}
            className="flex items-center gap-2 px-3 py-2 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 transition-colors text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            title="Import fields from CSV"
          >
            <Upload className="w-4 h-4" />
            Import
          </button>
          <input
            ref={importFileRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={handleImportFile}
          />
          <button
            onClick={() => {
              setForm(defaultForm);
              setShowModal(true);
            }}
            disabled={!selectedVertical}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" />
            Add Field
          </button>
        </div>
      </div>

      {/* Vertical Selector */}
      <div className="bg-white shadow-sm border border-slate-200 rounded-xl p-4">
        <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
          Vertical
        </label>
        {verticalsLoading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm">
            <RefreshCw className="w-4 h-4 animate-spin" />
            Loading verticals…
          </div>
        ) : verticals.length === 0 ? (
          <p className="text-sm text-slate-400">No verticals found.</p>
        ) : (
          <div className="relative inline-block">
            <select
              value={selectedVertical || ""}
              onChange={(e) => setSelectedVertical(e.target.value)}
              className="appearance-none pl-3 pr-8 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
            >
              {verticals.map((v) => (
                <option key={v.vertical_id} value={v.vertical_id}>
                  {v.vertical_name}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
        )}
      </div>

      {/* Field List */}
      <div className="bg-white shadow-sm border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-slate-400">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span className="text-sm">Loading fields…</span>
          </div>
        ) : fields.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400 gap-3">
            <Tag className="w-8 h-8 opacity-40" />
            <p className="text-sm">
              No fields defined for{" "}
              <span className="font-medium">{selectedVerticalName}</span>.
            </p>
            <button
              onClick={() => {
                setForm(defaultForm);
                setShowModal(true);
              }}
              className="text-sm text-blue-600 hover:underline"
            >
              Add the first field
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Field Name
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Required
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Active
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-48">
                    Description
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Order
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Allowed Values
                  </th>
                  <th className="px-4 py-3 w-10" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {fields.map((field) => (
                  <tr
                    key={field.id}
                    className={`hover:bg-slate-50 transition-colors ${
                      !field.is_active ? "opacity-50" : ""
                    }`}
                  >
                    {/* Field Name */}
                    <td className="px-4 py-3">
                      <span className="font-semibold text-slate-800">
                        {field.field_name}
                      </span>
                    </td>

                    {/* Type badge */}
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          TYPE_BADGE[field.field_type] ||
                          "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {field.field_type}
                      </span>
                    </td>

                    {/* Required badge */}
                    <td className="px-4 py-3">
                      {field.required ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-50 text-red-600">
                          <Check className="w-3 h-3" />
                          Yes
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-500">
                          No
                        </span>
                      )}
                    </td>

                    {/* Active toggle */}
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleActive(field)}
                        disabled={savingField === field.id}
                        className="flex items-center gap-1 text-xs transition-colors disabled:opacity-50"
                        title={field.is_active ? "Deactivate" : "Activate"}
                      >
                        {field.is_active ? (
                          <>
                            <ToggleRight className="w-5 h-5 text-green-500" />
                            <span className="text-green-600 font-medium">
                              On
                            </span>
                          </>
                        ) : (
                          <>
                            <ToggleLeft className="w-5 h-5 text-slate-400" />
                            <span className="text-slate-400">Off</span>
                          </>
                        )}
                      </button>
                    </td>

                    {/* Description (inline edit) */}
                    <td className="px-4 py-3 max-w-xs">
                      {editingDescription === field.id ? (
                        <div className="flex items-center gap-1">
                          <input
                            autoFocus
                            value={descriptionDraft}
                            onChange={(e) =>
                              setDescriptionDraft(e.target.value)
                            }
                            onBlur={() => commitDescription(field)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") commitDescription(field);
                              if (e.key === "Escape") {
                                setEditingDescription(null);
                                setDescriptionDraft("");
                              }
                            }}
                            className="flex-1 text-xs border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-0"
                          />
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setEditingDescription(field.id);
                            setDescriptionDraft(field.description || "");
                          }}
                          className="flex items-start gap-1 group text-left w-full"
                          title="Click to edit description"
                        >
                          <span className="text-slate-600 text-xs line-clamp-2 group-hover:text-slate-800 flex-1">
                            {field.description || (
                              <span className="text-slate-300 italic">
                                No description
                              </span>
                            )}
                          </span>
                          <Edit3 className="w-3 h-3 text-slate-300 group-hover:text-slate-500 flex-shrink-0 mt-0.5" />
                        </button>
                      )}
                    </td>

                    {/* Display Order (inline edit) */}
                    <td className="px-4 py-3">
                      {editingOrder === field.id ? (
                        <input
                          autoFocus
                          type="number"
                          value={orderDraft}
                          onChange={(e) => setOrderDraft(e.target.value)}
                          onBlur={() => commitOrder(field)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") commitOrder(field);
                            if (e.key === "Escape") {
                              setEditingOrder(null);
                              setOrderDraft("");
                            }
                          }}
                          className="w-16 text-xs border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      ) : (
                        <button
                          onClick={() => {
                            setEditingOrder(field.id);
                            setOrderDraft(String(field.display_order ?? 0));
                          }}
                          className="flex items-center gap-1 group"
                          title="Click to edit order"
                        >
                          <span className="text-slate-700 text-xs group-hover:text-slate-900">
                            {field.display_order ?? 0}
                          </span>
                          <Edit3 className="w-3 h-3 text-slate-300 group-hover:text-slate-500" />
                        </button>
                      )}
                    </td>

                    {/* Allowed Values */}
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1 max-w-xs">
                        {field.allowed_values &&
                        field.allowed_values.length > 0 ? (
                          <>
                            {field.allowed_values.map((val) => (
                              <span
                                key={val}
                                className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full text-xs"
                              >
                                {val}
                                <button
                                  onClick={() => removeAllowedValue(field, val)}
                                  disabled={savingField === field.id}
                                  className="ml-0.5 text-slate-400 hover:text-red-500 transition-colors disabled:opacity-50"
                                  title={`Remove "${val}"`}
                                >
                                  <X className="w-2.5 h-2.5" />
                                </button>
                              </span>
                            ))}
                          </>
                        ) : (
                          <span className="text-xs text-slate-300 italic">
                            Any
                          </span>
                        )}

                        {/* Add new value */}
                        {addingValueFor === field.id ? (
                          <div className="flex items-center gap-1">
                            <input
                              autoFocus
                              value={newValueDraft}
                              onChange={(e) => setNewValueDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") addAllowedValue(field);
                                if (e.key === "Escape") {
                                  setAddingValueFor(null);
                                  setNewValueDraft("");
                                }
                              }}
                              placeholder="value"
                              className="w-20 text-xs border border-blue-300 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                            <button
                              onClick={() => addAllowedValue(field)}
                              className="text-green-600 hover:text-green-700"
                              title="Confirm"
                            >
                              <Check className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => {
                                setAddingValueFor(null);
                                setNewValueDraft("");
                              }}
                              className="text-slate-400 hover:text-slate-600"
                              title="Cancel"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setAddingValueFor(field.id);
                              setNewValueDraft("");
                            }}
                            className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-dashed border-slate-300 text-slate-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
                            title="Add allowed value"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </td>

                    {/* Delete */}
                    <td className="px-4 py-3">
                      {confirmDeleteId === field.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => deleteField(field.id)}
                            className="p-1 text-red-600 hover:text-red-800 transition-colors"
                            title="Confirm delete"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setConfirmDeleteId(null)}
                            className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
                            title="Cancel"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmDeleteId(field.id)}
                          disabled={savingField === field.id}
                          className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Delete field"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Import Preview Modal */}
      {importRows !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl border border-slate-200 flex flex-col max-h-[80vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
              <div className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-blue-600" />
                <h3 className="text-base font-semibold text-slate-800">
                  Import Preview —{" "}
                  <span className="text-blue-600">{importRows.length}</span>{" "}
                  field{importRows.length !== 1 ? "s" : ""}
                </h3>
              </div>
              <button
                onClick={() => setImportRows(null)}
                disabled={importLoading}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="overflow-auto flex-1">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
                  <tr>
                    {[
                      "Field Name",
                      "Type",
                      "Req",
                      "Active",
                      "Order",
                      "Description",
                      "Allowed Values",
                    ].map((h) => (
                      <th
                        key={h}
                        className="text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {importRows.map((row, idx) => (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-semibold text-slate-800 whitespace-nowrap">
                        {row.field_name}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${TYPE_BADGE[row.field_type] || "bg-slate-100 text-slate-600"}`}
                        >
                          {row.field_type}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {row.required ? (
                          <Check className="w-3.5 h-3.5 text-green-500 inline" />
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {row.is_active ? (
                          <Check className="w-3.5 h-3.5 text-green-500 inline" />
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-600">
                        {row.display_order}
                      </td>
                      <td className="px-3 py-2 text-slate-600 max-w-[180px] truncate">
                        {row.description || (
                          <span className="text-slate-300 italic">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {row.allowed_values?.length ? (
                          <div className="flex flex-wrap gap-1">
                            {row.allowed_values.slice(0, 4).map((v) => (
                              <span
                                key={v}
                                className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs"
                              >
                                {v}
                              </span>
                            ))}
                            {row.allowed_values.length > 4 && (
                              <span className="text-slate-400 text-xs">
                                +{row.allowed_values.length - 4}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-300 italic">Any</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 flex-shrink-0">
              {importLoading ? (
                <div className="flex items-center gap-2 text-sm text-slate-600 w-full">
                  <RefreshCw className="w-4 h-4 animate-spin text-blue-500" />
                  <span>
                    Importing {importProgress} / {importRows.length}…
                  </span>
                  <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden ml-2">
                    <div
                      className="h-full bg-blue-500 rounded-full transition-all"
                      style={{
                        width: `${(importProgress / importRows.length) * 100}%`,
                      }}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-xs text-slate-400">
                    Existing fields with the same name will be skipped
                    (duplicate check enforced by DB).
                  </p>
                  <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                    <button
                      onClick={() => setImportRows(null)}
                      className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={submitImport}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      <Upload className="w-4 h-4" />
                      Import {importRows.length} Field
                      {importRows.length !== 1 ? "s" : ""}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Field Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg border border-slate-200">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <Tag className="w-5 h-5 text-blue-600" />
                <h3 className="text-base font-semibold text-slate-800">
                  Add Schema Field
                </h3>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={submitAddField} className="px-6 py-4 space-y-4">
              {/* Vertical (read-only display) */}
              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                  Vertical
                </label>
                <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600">
                  {selectedVerticalName}
                </div>
              </div>

              {/* Field Name */}
              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                  Field Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={form.field_name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, field_name: e.target.value }))
                  }
                  placeholder="e.g. author, source_url, category"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Field Type */}
              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                  Field Type
                </label>
                <div className="relative">
                  <select
                    value={form.field_type}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, field_type: e.target.value }))
                    }
                    className="w-full appearance-none pl-3 pr-8 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    {FIELD_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
              </div>

              {/* Required + Display Order row */}
              <div className="flex items-start gap-4">
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="modal-required"
                    checked={form.required}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, required: e.target.checked }))
                    }
                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                  <label
                    htmlFor="modal-required"
                    className="text-sm text-slate-700 cursor-pointer select-none"
                  >
                    Required
                  </label>
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={form.display_order}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, display_order: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                  Description
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, description: e.target.value }))
                  }
                  placeholder="Optional description of this field's purpose"
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                />
              </div>

              {/* Allowed Values */}
              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                  Allowed Values{" "}
                  <span className="text-slate-400 normal-case font-normal">
                    (comma-separated, leave blank for any)
                  </span>
                </label>
                <input
                  type="text"
                  value={form.allowed_values_raw}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      allowed_values_raw: e.target.value,
                    }))
                  }
                  placeholder="e.g. pdf, docx, html"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {form.allowed_values_raw && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {form.allowed_values_raw
                      .split(",")
                      .map((v) => v.trim())
                      .filter(Boolean)
                      .map((v) => (
                        <span
                          key={v}
                          className="inline-flex items-center px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs"
                        >
                          {v}
                        </span>
                      ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Adding…
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Add Field
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg border text-sm font-medium transition-all ${
            toast.type === "error"
              ? "bg-red-50 border-red-200 text-red-700"
              : "bg-green-50 border-green-200 text-green-700"
          }`}
        >
          {toast.type === "error" ? (
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          ) : (
            <Check className="w-4 h-4 flex-shrink-0" />
          )}
          {toast.message}
          <button
            onClick={() => setToast(null)}
            className="ml-1 opacity-60 hover:opacity-100 transition-opacity"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default MetadataSchemaManager;
